import {
  CHECK_FINDINGS,
  CHECK_SOURCES,
  type CheckFinding,
  type CheckInput,
  type CheckSeverity,
  CONTACT_ROLES,
  COUNTERPARTY_ROLES,
  type ContactRole,
  type CounterpartyRole,
  CP_DOC_KINDS,
  type CpDocKind,
  type StopInput,
  type SupplierCard,
  type SupplierInput,
  type SupplierSummary,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, tenge } from '../../lib/format';
import { useCompanyWide } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { ListSettingsLink } from '../reports/ListSettingsLink';

const suppliersQuery = (q: { q?: string; flag?: 'stop' | 'docs' }) =>
  queryOptions({
    queryKey: ['suppliers', q],
    queryFn: () => api<SupplierSummary[]>('/suppliers', { query: q }),
  });
const supplierQuery = (id: string) =>
  queryOptions({ queryKey: ['supplier', id], queryFn: () => api<SupplierCard>(`/suppliers/${id}`) });

const tags = (s: string) =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

function Flags({ s }: { s: SupplierSummary }) {
  return (
    <span className="flex flex-wrap gap-1">
      {s.stopListed ? <Badge tone="danger">стоп-лист</Badge> : null}
      {s.docsExpired ? <Badge tone="danger">документы истекли: {s.docsExpired}</Badge> : null}
      {s.docsExpiring ? <Badge tone="warning">документы истекают: {s.docsExpiring}</Badge> : null}
    </span>
  );
}

function SupplierForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: SupplierCard;
  onSaved: (c: SupplierCard) => void;
  onCancel?: () => void;
}) {
  const [f, setF] = useState({
    name: initial?.name ?? '',
    shortName: initial?.shortName ?? '',
    bin: initial?.bin ?? '',
    roles: initial?.roles ?? (['supplier'] as CounterpartyRole[]),
    categories: (initial?.categories ?? []).join(', '),
    regions: (initial?.regions ?? []).join(', '),
    address: initial?.address ?? '',
    phone: initial?.phone ?? '',
    email: initial?.email ?? '',
    bank: initial?.bank ?? '',
    iik: initial?.iik ?? '',
    bik: initial?.bik ?? '',
    note: initial?.note ?? '',
  });
  const similar = useQuery({
    queryKey: ['suppliers', 'similar', f.name, f.bin],
    queryFn: () =>
      api<SupplierSummary[]>('/suppliers/similar', { query: { name: f.name, bin: f.bin || undefined } }),
    enabled: !initial && (f.name.trim().length >= 4 || f.bin.length === 12),
  });
  const m = useMutation({
    mutationFn: () => {
      const body: SupplierInput = {
        name: f.name.trim(),
        shortName: f.shortName.trim() || null,
        bin: f.bin.trim() || null,
        roles: f.roles,
        categories: tags(f.categories),
        regions: tags(f.regions),
        address: f.address.trim() || null,
        phone: f.phone.trim() || null,
        email: f.email.trim() || null,
        bank: f.bank.trim() || null,
        iik: f.iik.trim() || null,
        bik: f.bik.trim() || null,
        note: f.note.trim() || null,
      };
      return initial
        ? api<SupplierCard>(`/suppliers/${initial.id}`, { method: 'PATCH', body })
        : api<SupplierCard>('/suppliers', { body });
    },
    onSuccess: (c) => {
      toast(initial ? 'Карточка сохранена' : 'Контрагент заведён');
      onSaved(c);
    },
  });
  const dups = (similar.data ?? []).filter((s) => s.id !== initial?.id);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Наименование" className="sm:col-span-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="БИН или ИИН">
          <Input inputMode="numeric" value={f.bin} onChange={(e) => setF({ ...f, bin: e.target.value })} />
        </Field>
      </div>
      {dups.length ? (
        <div className="rounded-md bg-warning-soft px-4 py-3 text-[13px]">
          <p className="font-medium">Похожие в реестре — проверьте, не дубль ли это (КА-02):</p>
          <ul className="mt-1 list-disc pl-5">
            {dups.map((s) => (
              <li key={s.id}>
                <Link to={`/suppliers/${s.id}`} className="hover:underline">
                  {s.name}
                </Link>
                {s.bin ? `, БИН ${s.bin}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <fieldset className="flex flex-wrap gap-x-5 gap-y-2">
        <legend className="mb-1.5 text-[13px] font-medium">Роли</legend>
        {(Object.keys(COUNTERPARTY_ROLES) as CounterpartyRole[]).map((r) => (
          <Checkbox
            key={r}
            label={COUNTERPARTY_ROLES[r]}
            checked={f.roles.includes(r)}
            onChange={(e) =>
              setF({ ...f, roles: e.target.checked ? [...f.roles, r] : f.roles.filter((x) => x !== r) })
            }
          />
        ))}
      </fieldset>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Категории ТМЦ и работ" hint="через запятую: щебень, бетон, арматура">
          <Input value={f.categories} onChange={(e) => setF({ ...f, categories: e.target.value })} />
        </Field>
        <Field label="Регионы" hint="через запятую">
          <Input value={f.regions} onChange={(e) => setF({ ...f, regions: e.target.value })} />
        </Field>
        <Field label="Адрес">
          <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </Field>
        <Field label="Телефон, почта">
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
            <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          </div>
        </Field>
        <Field label="Банк">
          <Input value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })} />
        </Field>
        <Field label="ИИК и БИК">
          <div className="grid grid-cols-[2fr_1fr] gap-2">
            <Input value={f.iik} onChange={(e) => setF({ ...f, iik: e.target.value })} />
            <Input value={f.bik} onChange={(e) => setF({ ...f, bik: e.target.value })} />
          </div>
        </Field>
      </div>
      <Field label="Примечание">
        <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      <ErrorBox error={m.error} />
      <div className="flex gap-2">
        <Button
          variant="primary"
          loading={m.isPending}
          disabled={f.name.trim().length < 2 || !f.roles.length}
          onClick={() => m.mutate()}
        >
          {initial ? 'Сохранить карточку' : 'Завести контрагента'}
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Отмена
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function SuppliersPage() {
  const navigate = useNavigate();
  const canEdit = useCompanyWide('suppliers.edit', 'procurement.rfq.manage');
  const [q, setQ] = useState('');
  const [flag, setFlag] = useState<'' | 'stop' | 'docs'>('');
  const [creating, setCreating] = useState(false);
  const list = useQuery(
    suppliersQuery({ q: q.trim().length >= 2 ? q.trim() : undefined, flag: flag || undefined }),
  );
  return (
    <Page>
      <PageHeader
        title="Поставщики и контрагенты"
        subtitle="Один реестр на группу: роли, категории, реквизиты, документы со сроком действия, представители, стоп-лист"
        actions={
          <>
            <ListSettingsLink source="counterparties" />
            {canEdit ? (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                Новый контрагент
              </Button>
            ) : null}
          </>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          type="search"
          className="h-9 max-w-[340px]"
          placeholder="Название или БИН"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select className="h-9 w-auto" value={flag} onChange={(e) => setFlag(e.target.value as typeof flag)}>
          <option value="">Все</option>
          <option value="stop">В стоп-листе</option>
          <option value="docs">Истекают или истекли документы</option>
        </Select>
      </div>
      <Panel bodyClassName="p-0">
        {list.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : list.isError ? (
          <ErrorBox error={list.error} className="m-5" />
        ) : list.data.length ? (
          <ScrollX>
            <table className="w-full min-w-[860px] text-[14px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2.5 pr-3 pl-5 font-medium">Контрагент</th>
                  <th className="py-2.5 pr-3 font-medium">БИН</th>
                  <th className="py-2.5 pr-3 font-medium">Роли и категории</th>
                  <th className="py-2.5 pr-3 text-right font-medium">КП</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Договоры</th>
                  <th className="py-2.5 pr-5 font-medium">Отметки</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((s) => (
                  <tr key={s.id} className="border-t border-line align-top">
                    <td className="py-2.5 pr-3 pl-5">
                      <Link to={`/suppliers/${s.id}`} className="font-medium text-ink hover:underline">
                        {s.name}
                      </Link>
                    </td>
                    <td className="num py-2.5 pr-3 text-ink-2">{s.bin ?? '—'}</td>
                    <td className="py-2.5 pr-3 text-[13px]">
                      {s.roles.map((r) => COUNTERPARTY_ROLES[r]).join(', ')}
                      {s.categories.length ? (
                        <div className="text-ink-2">{s.categories.join(', ')}</div>
                      ) : null}
                    </td>
                    <td className="num py-2.5 pr-3 text-right">{s.offers}</td>
                    <td className="num py-2.5 pr-3 text-right">{s.contracts}</td>
                    <td className="py-2.5 pr-5">
                      <Flags s={s} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        ) : (
          <div className="p-5">
            <EmptyState title="Никого не нашлось">
              Контрагенты появляются в реестре при заведении и при загрузке КП.
            </EmptyState>
          </div>
        )}
      </Panel>
      {creating ? (
        <Dialog open onOpenChange={(v) => !v && setCreating(false)} title="Новый контрагент" width="lg">
          <SupplierForm
            onCancel={() => setCreating(false)}
            onSaved={(c) => {
              setCreating(false);
              navigate({ to: `/suppliers/${c.id}` });
            }}
          />
        </Dialog>
      ) : null}
    </Page>
  );
}

function Docs({
  c,
  canEdit,
  onSaved,
}: {
  c: SupplierCard;
  canEdit: boolean;
  onSaved: (c: SupplierCard) => void;
}) {
  type Row = {
    k: string;
    kind: CpDocKind;
    number: string;
    issuedOn: string;
    validUntil: string;
    note: string;
  };
  const toRows = (): Row[] =>
    c.docs.map((d) => ({
      k: d.id,
      kind: d.kind,
      number: d.number ?? '',
      issuedOn: d.issuedOn ?? '',
      validUntil: d.validUntil ?? '',
      note: d.note ?? '',
    }));
  const [rows, setRows] = useState<Row[]>(toRows);
  // biome-ignore lint/correctness/useExhaustiveDependencies: новые данные с сервера
  useEffect(() => setRows(toRows()), [c]);
  const m = useMutation({
    mutationFn: () =>
      api<SupplierCard>(`/suppliers/${c.id}/docs`, {
        method: 'PUT',
        body: {
          docs: rows.map((r) => ({
            id: c.docs.some((d) => d.id === r.k) ? r.k : undefined,
            kind: r.kind,
            number: r.number.trim() || null,
            issuedOn: r.issuedOn || null,
            validUntil: r.validUntil || null,
            note: r.note.trim() || null,
          })),
        },
      }),
    onSuccess: (v) => {
      onSaved(v);
      toast('Документы сохранены');
    },
  });
  const state = (d: Row) => c.docs.find((x) => x.id === d.k)?.state;
  const set = (k: string, p: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.k === k ? { ...r, ...p } : r)));
  return (
    <Panel
      title="Документы со сроком действия"
      meta="КА-04"
      bodyClassName="p-0"
      actions={
        canEdit ? (
          <>
            <Button
              variant="ghost"
              icon={<Plus className="size-4" />}
              onClick={() =>
                setRows([
                  ...rows,
                  { k: `n${Date.now()}`, kind: 'vat', number: '', issuedOn: '', validUntil: '', note: '' },
                ])
              }
            >
              Документ
            </Button>
            <Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>
              Сохранить
            </Button>
          </>
        ) : null
      }
    >
      <p className="px-5 pt-4 text-[13px] text-ink-2">
        За 30 дней до окончания — напоминание в «Ждёт меня»; истёкший документ отмечается в закупках и
        договорах.
      </p>
      <ErrorBox error={m.error} className="m-4" />
      {rows.length ? (
        <ScrollX>
          <table className="w-full min-w-[640px] text-[13px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2.5 pr-2 pl-5 font-medium">Вид</th>
                <th className="py-2.5 pr-2 font-medium">Номер</th>
                <th className="py-2.5 pr-2 font-medium">Выдан</th>
                <th className="py-2.5 pr-2 font-medium">Действует до</th>
                <th className="py-2.5 pr-2 font-medium">Состояние</th>
                <th className="py-2.5 pr-5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = state(r);
                return (
                  <tr key={r.k} className="border-t border-line">
                    <td className="py-1.5 pr-2 pl-5">
                      <Select
                        disabled={!canEdit}
                        value={r.kind}
                        onChange={(e) => set(r.k, { kind: e.target.value as CpDocKind })}
                      >
                        {(Object.keys(CP_DOC_KINDS) as CpDocKind[]).map((k) => (
                          <option key={k} value={k}>
                            {CP_DOC_KINDS[k]}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        disabled={!canEdit}
                        value={r.number}
                        onChange={(e) => set(r.k, { number: e.target.value })}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        disabled={!canEdit}
                        type="date"
                        value={r.issuedOn}
                        onChange={(e) => set(r.k, { issuedOn: e.target.value })}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        disabled={!canEdit}
                        type="date"
                        value={r.validUntil}
                        onChange={(e) => set(r.k, { validUntil: e.target.value })}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      {st === 'expired' ? (
                        <Badge tone="danger">истёк</Badge>
                      ) : st === 'expiring' ? (
                        <Badge tone="warning">истекает</Badge>
                      ) : st === 'valid' ? (
                        <Badge tone="success">действует</Badge>
                      ) : st === 'open' ? (
                        <Badge tone="neutral">бессрочный</Badge>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-5 text-right">
                      {canEdit ? (
                        <IconButton
                          label="Удалить документ"
                          onClick={() => setRows(rows.filter((x) => x.k !== r.k))}
                        >
                          <Trash2 className="size-4" />
                        </IconButton>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollX>
      ) : (
        <p className="p-5 text-[14px] text-ink-2">Документов нет.</p>
      )}
    </Panel>
  );
}

function Contacts({
  c,
  canEdit,
  onSaved,
}: {
  c: SupplierCard;
  canEdit: boolean;
  onSaved: (c: SupplierCard) => void;
}) {
  type Row = { k: string; fullName: string; role: ContactRole; phone: string; email: string };
  const toRows = (): Row[] =>
    c.contacts.map((x) => ({
      k: x.id,
      fullName: x.fullName,
      role: x.role,
      phone: x.phone ?? '',
      email: x.email ?? '',
    }));
  const [rows, setRows] = useState<Row[]>(toRows);
  // biome-ignore lint/correctness/useExhaustiveDependencies: новые данные с сервера
  useEffect(() => setRows(toRows()), [c]);
  const m = useMutation({
    mutationFn: () =>
      api<SupplierCard>(`/suppliers/${c.id}/contacts`, {
        method: 'PUT',
        body: {
          contacts: rows
            .filter((r) => r.fullName.trim().length >= 2)
            .map((r) => ({
              id: c.contacts.some((x) => x.id === r.k) ? r.k : undefined,
              fullName: r.fullName.trim(),
              role: r.role,
              phone: r.phone.trim() || null,
              email: r.email.trim() || null,
            })),
        },
      }),
    onSuccess: (v) => {
      onSaved(v);
      toast('Представители сохранены');
    },
  });
  const set = (k: string, p: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.k === k ? { ...r, ...p } : r)));
  return (
    <Panel
      title="Представители"
      meta="КА-05"
      actions={
        canEdit ? (
          <>
            <Button
              variant="ghost"
              icon={<Plus className="size-4" />}
              onClick={() =>
                setRows([
                  ...rows,
                  { k: `n${Date.now()}`, fullName: '', role: 'manager', phone: '', email: '' },
                ])
              }
            >
              Представитель
            </Button>
            <Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>
              Сохранить
            </Button>
          </>
        ) : null
      }
    >
      {rows.length ? (
        <div className="flex flex-col gap-2">
          {rows.map((r) => (
            <div key={r.k} className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr_1.5fr_auto]">
              <Input
                disabled={!canEdit}
                placeholder="ФИО"
                value={r.fullName}
                onChange={(e) => set(r.k, { fullName: e.target.value })}
              />
              <Select
                disabled={!canEdit}
                value={r.role}
                onChange={(e) => set(r.k, { role: e.target.value as ContactRole })}
              >
                {(Object.keys(CONTACT_ROLES) as ContactRole[]).map((k) => (
                  <option key={k} value={k}>
                    {CONTACT_ROLES[k]}
                  </option>
                ))}
              </Select>
              <Input
                disabled={!canEdit}
                placeholder="телефон"
                value={r.phone}
                onChange={(e) => set(r.k, { phone: e.target.value })}
              />
              <Input
                disabled={!canEdit}
                placeholder="почта"
                value={r.email}
                onChange={(e) => set(r.k, { email: e.target.value })}
              />
              {canEdit ? (
                <IconButton label="Удалить" onClick={() => setRows(rows.filter((x) => x.k !== r.k))}>
                  <Trash2 className="size-4" />
                </IconButton>
              ) : (
                <span />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[14px] text-ink-2">Представителей нет.</p>
      )}
      <ErrorBox error={m.error} className="mt-3" />
    </Panel>
  );
}

const CHECK_TONE = { ok: 'success', attention: 'warning', stop: 'danger' } as const;
const CHECK_LABEL = { ok: 'без замечаний', attention: 'внимание', stop: 'стоп' } as const;

function Checks({
  c,
  canEdit,
  onSaved,
}: {
  c: SupplierCard;
  canEdit: boolean;
  onSaved: (c: SupplierCard) => void;
}) {
  // КА-10: решение по «стопу» принимает Директор
  const canDecide = useCompanyWide('budget.approve');
  const [picked, setPicked] = useState<CheckFinding[]>([]);
  const [note, setNote] = useState('');
  const [why, setWhy] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const body: Partial<CheckInput> = {
        source: 'manual',
        findings: picked.map((kind) => ({ kind })),
        note: note.trim() || null,
      };
      return api<SupplierCard>(`/suppliers/${c.id}/checks`, { body });
    },
    onSuccess: (v) => {
      onSaved(v);
      setPicked([]);
      setNote('');
      toast('Проверка записана');
    },
  });
  const [draft, setDraft] = useState<Record<string, CheckSeverity> | null>(null);
  const saveRules = useMutation({
    mutationFn: (severities: Record<string, CheckSeverity>) =>
      api<Record<string, CheckSeverity>>('/suppliers/check-rules', { method: 'PUT', body: { severities } }),
    onSuccess: (rules) => {
      onSaved({ ...c, checkRules: rules });
      setDraft(null);
      toast('Правила проверки сохранены');
    },
  });
  const resolve = useMutation({
    mutationFn: (checkId: string) =>
      api<SupplierCard>(`/suppliers/${c.id}/checks/${checkId}/resolve`, { body: { reason: why.trim() } }),
    onSuccess: (v) => {
      onSaved(v);
      setWhy('');
      toast('Решение записано');
    },
  });
  return (
    <Panel title="Проверка контрагента" meta="КА-07–КА-10">
      <p className="mb-3 text-[13px] text-ink-2">
        Результат проверки по БИН: «стоп» (реестр недобросовестных, лжепредприятие, ограничение ЭСФ, санкции)
        закрывает выбор в закупке и договор до решения Директора с причиной. Источники Kompra и КГД ещё не
        подключены — результат вносится вручную.
      </p>
      {c.checkStop ? (
        <p className="mb-3 rounded-md border border-danger/40 px-3 py-2 text-[14px] text-ink">
          Есть проверка со «стопом» без решения: выбор в закупке и договор закрыты, пока Директор не решит по
          каждой.
        </p>
      ) : null}
      {c.checks.length ? (
        <ul className="mb-4 flex flex-col gap-3 text-[14px]">
          {c.checks.map((k) => (
            <li key={k.id}>
              <Badge tone={CHECK_TONE[k.result]}>{CHECK_LABEL[k.result]}</Badge> {date(k.checkedOn)} ·{' '}
              {CHECK_SOURCES[k.source as keyof typeof CHECK_SOURCES]?.title ?? k.source}
              {k.findings.length ? (
                <ul className="ml-4 list-disc text-[13px] text-ink-2">
                  {k.findings.map((f) => (
                    <li key={`${f.kind}-${f.text ?? ''}`}>
                      {f.title}
                      {f.text ? `: ${f.text}` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
              {k.note ? <span className="block text-[13px] text-ink-2">{k.note}</span> : null}
              <span className="block text-[12px] text-ink-2">{k.byName ?? '—'}</span>
              <a
                className="inline-flex min-h-[44px] items-center text-[13px] font-medium text-ink underline md:min-h-9"
                href={`/api/v1/suppliers/${c.id}/checks/${k.id}/report.pdf`}
                target="_blank"
                rel="noreferrer"
              >
                Отчёт в PDF
              </a>
              {k.resolvedAt ? (
                <span className="block text-[13px]">
                  Решение Директора: {k.resolvedReason} ({k.resolvedByName ?? '—'}, {dateTime(k.resolvedAt)})
                </span>
              ) : k.result === 'stop' && canDecide ? (
                <span className="mt-2 flex flex-wrap items-end gap-2">
                  <Field label="Причина решения допустить">
                    <Input value={why} onChange={(e) => setWhy(e.target.value)} />
                  </Field>
                  <Button
                    variant="secondary"
                    loading={resolve.isPending}
                    disabled={why.trim().length < 3}
                    onClick={() => resolve.mutate(k.id)}
                  >
                    Допустить, несмотря на «стоп»
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-[14px] text-ink-2">Проверок не было.</p>
      )}
      {canEdit ? (
        <div className="flex flex-col gap-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {(Object.keys(CHECK_FINDINGS) as CheckFinding[]).map((k) => (
              <Checkbox
                key={k}
                label={CHECK_FINDINGS[k].title}
                description={
                  c.checkRules[k] === 'stop'
                    ? 'стоп'
                    : c.checkRules[k] === 'attention'
                      ? 'внимание'
                      : undefined
                }
                checked={picked.includes(k)}
                onChange={(e) => setPicked(e.target.checked ? [...picked, k] : picked.filter((x) => x !== k))}
              />
            ))}
          </div>
          <Field label="Примечание">
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div>
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              {picked.length ? 'Записать проверку с находками' : 'Записать проверку без замечаний'}
            </Button>
          </div>
        </div>
      ) : null}
      {canDecide ? (
        <details className="mt-4 text-[14px]">
          <summary className="min-h-11 cursor-pointer py-2 font-medium">
            Правила проверки компании (КА-10)
          </summary>
          <p className="mb-2 text-[13px] text-ink-2">
            Важность каждой находки: «стоп» закрывает выбор и договор до решения Директора, «внимание» только
            подсвечивается. Новые правила действуют на новые проверки; внесённые раньше не пересчитываются.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {(Object.keys(CHECK_FINDINGS) as CheckFinding[]).map((k) => (
              <Field key={k} label={CHECK_FINDINGS[k].title}>
                <select
                  className="min-h-11 rounded-md border border-line bg-surface px-2"
                  value={(draft ?? c.checkRules)[k]}
                  onChange={(e) =>
                    setDraft({ ...(draft ?? c.checkRules), [k]: e.target.value as CheckSeverity })
                  }
                >
                  <option value="stop">стоп</option>
                  <option value="attention">внимание</option>
                  <option value="info">справочно</option>
                </select>
              </Field>
            ))}
          </div>
          <Button
            className="mt-3"
            loading={saveRules.isPending}
            disabled={!draft}
            onClick={() => draft && saveRules.mutate(draft)}
          >
            Сохранить правила
          </Button>
        </details>
      ) : null}
      <ErrorBox error={save.error ?? resolve.error ?? saveRules.error} className="mt-3" />
    </Panel>
  );
}

function Stoplist({ c, onSaved }: { c: SupplierCard; onSaved: (c: SupplierCard) => void }) {
  // КА-13: решение Директора — право «Утверждение бюджета» компании или юрлица
  const canDecide = useCompanyWide('budget.approve');
  const [f, setF] = useState({ reason: '', basis: '', until: '' });
  const m = useMutation({
    mutationFn: () => {
      const body: StopInput = {
        action: c.stopListed ? 'remove' : 'add',
        reason: f.reason.trim(),
        basis: f.basis.trim() || null,
        until: f.until || null,
      };
      return api<SupplierCard>(`/suppliers/${c.id}/stoplist`, { body });
    },
    onSuccess: (v) => {
      onSaved(v);
      setF({ reason: '', basis: '', until: '' });
      toast(v.stopListed ? 'Внесён в стоп-лист' : 'Снят со стоп-листа');
    },
  });
  return (
    <Panel title="Стоп-лист" meta="КА-13">
      <p className="mb-3 text-[13px] text-ink-2">
        Вносит и снимает Директор с причиной; контрагент из стоп-листа не получает запрос КП и не участвует в
        выборе.
      </p>
      {c.stoplist.length ? (
        <ul className="mb-4 flex flex-col gap-2 text-[14px]">
          {c.stoplist.map((s) => (
            <li key={s.id}>
              <Badge tone={s.action === 'add' ? 'danger' : 'success'}>
                {s.action === 'add' ? 'внесён' : 'снят'}
              </Badge>{' '}
              {s.reason}
              {s.basis ? ` · основание: ${s.basis}` : ''}
              {s.until ? ` · до ${date(s.until)}` : ''}
              <span className="block text-[12px] text-ink-2">
                {s.byName ?? '—'}, {dateTime(s.at)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-[14px] text-ink-2">Решений не было.</p>
      )}
      {canDecide ? (
        <div className="grid gap-3 sm:grid-cols-[2fr_1.5fr_1fr_auto] sm:items-end">
          <Field label={c.stopListed ? 'Причина снятия' : 'Причина'}>
            <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
          </Field>
          <Field label="Документ-основание">
            <Input value={f.basis} onChange={(e) => setF({ ...f, basis: e.target.value })} />
          </Field>
          <Field label="Срок">
            <Input
              type="date"
              value={f.until}
              disabled={c.stopListed}
              onChange={(e) => setF({ ...f, until: e.target.value })}
            />
          </Field>
          <Button
            variant={c.stopListed ? 'secondary' : 'danger'}
            loading={m.isPending}
            disabled={f.reason.trim().length < 3}
            onClick={() => m.mutate()}
          >
            {c.stopListed ? 'Снять со стоп-листа' : 'Внести в стоп-лист'}
          </Button>
        </div>
      ) : null}
      <ErrorBox error={m.error} className="mt-3" />
    </Panel>
  );
}

export function SupplierPage() {
  const { supplierId } = useParams({ strict: false }) as { supplierId: string };
  const qc = useQueryClient();
  const canEdit = useCompanyWide('suppliers.edit', 'procurement.rfq.manage');
  const q = useQuery(supplierQuery(supplierId));
  useCrumbs(q.data ? [{ label: q.data.shortName || q.data.name, title: q.data.name }] : null, q.error);
  const [editing, setEditing] = useState(false);
  const apply = (v: SupplierCard) => {
    qc.setQueryData(supplierQuery(supplierId).queryKey, v);
    qc.invalidateQueries({ queryKey: ['suppliers'] });
  };
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const c = q.data;
  return (
    <Page>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.01em] text-ink">{c.name}</h1>
        <Flags s={c} />
      </div>
      <p className="mt-1 text-[14px] text-ink-2">
        {c.bin ? `БИН ${c.bin} · ` : ''}
        {c.roles.map((r) => COUNTERPARTY_ROLES[r]).join(', ')}
        {c.categories.length ? ` · ${c.categories.join(', ')}` : ''}
        {c.regions.length ? ` · ${c.regions.join(', ')}` : ''}
      </p>
      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-6">
          <Panel
            title="Реквизиты"
            actions={canEdit && !editing ? <Button onClick={() => setEditing(true)}>Изменить</Button> : null}
          >
            {editing ? (
              <SupplierForm
                initial={c}
                onCancel={() => setEditing(false)}
                onSaved={(v) => {
                  apply(v);
                  setEditing(false);
                }}
              />
            ) : (
              <dl className="grid gap-x-6 gap-y-2 text-[14px] sm:grid-cols-[160px_1fr]">
                <dt className="text-ink-2">Адрес</dt>
                <dd>{c.address ?? '—'}</dd>
                <dt className="text-ink-2">Телефон, почта</dt>
                <dd>{[c.phone, c.email].filter(Boolean).join(', ') || '—'}</dd>
                <dt className="text-ink-2">Банк</dt>
                <dd>{c.bank ?? '—'}</dd>
                <dt className="text-ink-2">ИИК, БИК</dt>
                <dd className="num">{[c.iik, c.bik].filter(Boolean).join(', ') || '—'}</dd>
                <dt className="text-ink-2">Примечание</dt>
                <dd>{c.note ?? '—'}</dd>
              </dl>
            )}
          </Panel>
          <Docs c={c} canEdit={canEdit} onSaved={apply} />
          <Contacts c={c} canEdit={canEdit} onSaved={apply} />
          <Checks c={c} canEdit={canEdit} onSaved={apply} />
          <Stoplist c={c} onSaved={apply} />
        </div>
        <div className="flex flex-col gap-6">
          <Panel title="КП" meta={`${c.offerList.length}`} bodyClassName="p-0">
            {c.offerList.length ? (
              <ul className="divide-y divide-line">
                {c.offerList.map((o) => (
                  <li key={`${o.requestId}-${o.receivedOn}`} className="px-5 py-2.5 text-[14px]">
                    <Link to={`/procurement/${o.requestId}`} className="font-medium hover:underline">
                      {o.requestNumber} {o.title}
                    </Link>
                    <span className="block text-[12px] text-ink-2">
                      {o.projectCode}
                      {o.receivedOn ? ` · ${date(o.receivedOn)}` : ''}
                      {o.isSelected ? ' · выбран' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-5 text-[14px] text-ink-2">КП не было.</p>
            )}
          </Panel>
          <Panel
            title="Договоры"
            meta={c.paidTotal !== null ? `оплачено ${tenge(c.paidTotal)}` : undefined}
            bodyClassName="p-0"
          >
            {c.contractList.length ? (
              <ul className="divide-y divide-line">
                {c.contractList.map((x) => (
                  <li key={x.id} className="flex justify-between gap-3 px-5 py-2.5 text-[14px]">
                    <Link to={`/contracts/${x.id}`} className="min-w-0 truncate font-medium hover:underline">
                      {x.number} {x.subject}
                    </Link>
                    <span className={clsx('num shrink-0', x.amount === null && 'text-ink-3')}>
                      {tenge(x.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-5 text-[14px] text-ink-2">Договоров нет.</p>
            )}
          </Panel>
        </div>
      </div>
    </Page>
  );
}
